/**
 * Company Overview tab (CR-6247).
 *
 * Read-only view of everything the Organisation Structure edit pop-up captures,
 * laid out to match the supplier profile's Company Information card so the two
 * profiles read the same way. Editing stays in the Organisation edit pop-up,
 * which is the ticket's designated write surface.
 */
import type { ReactNode } from 'react';
import {
  Building2,
  Mail,
  Globe,
  MapPin,
  Users,
  Calendar,
  DollarSign,
  Phone,
  Hash,
  FileText,
  Banknote,
  type LucideIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import type { Company } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';

interface OverviewTabProps {
  company: Company;
  /**
   * CR-6250: hide Company Number and Annual Revenue. Set for a supplier
   * viewing one of its customers — the backend strips both fields from the
   * payload for that caller, so without this they would render as
   * "Not specified" and read as missing data rather than withheld.
   */
  hideCommercialFields?: boolean;
}

const NOT_SPECIFIED = 'Not specified';

function InfoRow({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="h-4 w-4 text-muted-foreground mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{label}</p>
        <div className="text-sm text-muted-foreground break-words">{children}</div>
      </div>
    </div>
  );
}

/** Join the address parts that are actually set, in postal order. */
function buildAddress(company: Company): string {
  return [
    company.address_line_1,
    company.address_line_2,
    company.city,
    company.state,
    company.postal_code,
    company.country,
  ]
    .filter((part) => !!part && part.trim())
    .join(', ');
}

/**
 * Render the company's saved details.
 *
 * @param company - the company being viewed
 */
export function OverviewTab({ company, hideCommercialFields = false }: OverviewTabProps) {
  const { formatCurrency } = useFormatters();
  const address = buildAddress(company);
  const profile = company.profile;
  // Annual revenue is stored in the company's own currency, so it renders with
  // that symbol rather than the viewer's preference (no FX conversion).
  const currency = company.effective_currency || company.base_currency || undefined;
  const revenue = profile?.annual_revenue;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Company Information
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Two columns, matching the supplier profile (CR-6681): left = identity
            and contact, right = the business / financial fields. */}
        <div className="grid grid-cols-1 gap-x-8 gap-y-4 md:grid-cols-2">
          <div className="space-y-4">
            <InfoRow icon={Mail} label="Company Email">
              {company.primary_email || NOT_SPECIFIED}
            </InfoRow>
            {company.primary_phone && (
              <InfoRow icon={Phone} label="Telephone">{company.primary_phone}</InfoRow>
            )}
            {!hideCommercialFields && (
              <InfoRow icon={Hash} label="Company Number">
                {company.registration_number || NOT_SPECIFIED}
              </InfoRow>
            )}
            <InfoRow icon={MapPin} label="Location">
              {address || NOT_SPECIFIED}
            </InfoRow>
            <InfoRow icon={Globe} label="Website">
              {company.website ? (
                <a
                  href={company.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {company.website}
                </a>
              ) : (
                NOT_SPECIFIED
              )}
            </InfoRow>
          </div>

          <div className="space-y-4">
            <InfoRow icon={Banknote} label="Currency">
              {currency || NOT_SPECIFIED}
            </InfoRow>
            {!hideCommercialFields && (
              <InfoRow icon={DollarSign} label="Annual Revenue">
                {revenue ? formatCurrency(Number(revenue), currency) : NOT_SPECIFIED}
              </InfoRow>
            )}
            <InfoRow icon={Users} label="Employees">
              {profile?.employee_count
                ? `${profile.employee_count} employees`
                : NOT_SPECIFIED}
            </InfoRow>
            <InfoRow icon={Calendar} label="Established">
              {profile?.year_established || NOT_SPECIFIED}
            </InfoRow>
            {company.description && (
              <InfoRow icon={FileText} label="Description">{company.description}</InfoRow>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default OverviewTab;
